

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"

import FieldMapping from "./FieldMapping.vue"
import EventMapping from "./EventMapping.vue"
import IntegrationConfiguration from "./IntegrationConfiguration.vue"
import OutputConfiguration from "./OutputConfiguration.vue"

import { IntegrationSettings, IntegrationWithSettings, IntegrationColumn } from "@/types/integration"

import api from "@/services/api"
import integrationApi from "@/services/api_Integrations"

import cloneDeep from "lodash/cloneDeep"
import { Field, Team, Template } from "@/types"
import { CaseColumn, caseColumns } from "@/types/caseColumns"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"skeleton": SkeletonLoader,
		"configuration": IntegrationConfiguration,
		"event-mapping": EventMapping,
		"field-mapping": FieldMapping,
		"output-configuration": OutputConfiguration
	}
})
export default class IntegrationSetup extends Vue {
	$refs!: {
		dataTable: DataTable
	}

	@setup.State
	fields!: Field[];

	@setup.Getter("getTeams")
	teams!: Team[];

	@setup.Mutation
	addField!: (field: Field) => void;

	private loading: boolean = false;
	private integrations: IntegrationWithSettings[] = [];
	private selectedIntegration: IntegrationWithSettings | null = null;

	private templates: Template[] = []

	get columns () {
		return [
			{ key: "settings.enabled", label: "Enabled", dataType: "checkbox", sortable: true },
			{ key: "integration.title", label: "Title", dataType: "text", sortable: true, readonly: true },
			{ key: "integration.description", label: "Description", dataType: "text-area", sortable: true, readonly: true }
		]
	}

	get caseColumns (): CaseColumn[] {
		return [
			...caseColumns,
			...this.fields.map(field => {
				return {
					key: "fields." + field.id,
					label: field.title,
					dataType: field.dataType!,
					dynamicField: true
				}
			})
		]
	}

	public async mounted () {
		this.loading = true
		try {
			this.integrations = await integrationApi.integrationsGet()

			api.categories().then(templates => this.templates = templates)
		} finally {
			this.loading = false
		}
	}

	selectIntegration (integration: IntegrationWithSettings) {
		const integrationClone = cloneDeep(integration)
		if (!integrationClone.settings) {
			integrationClone.settings = {
				integrationId: integrationClone.integration.id,
				enabled: true,
				configuration: {},
				inFieldMapping: {},
				eventMapping: {},
				outConfiguration: {}
			}
		}

		return this.selectedIntegration = integrationClone
	}

	async createField (field: IntegrationColumn): Promise<string> {
		const newField = {
			title: field.title,
			description: `${field.title} from ${this.selectedIntegration!.integration.title} `,
			dataType: field.dataType,
			dataSourceId: null,
			dataSourceTitle: null,
			definitionId: null,
			definitionTitle: null,
			allowFiles: false,
			sortByDescending: false,
			permissions: {
				record: { view: [], edit: [] },
				case: { view: [], edit: [] }
			},
			extensions: field.dataType === "user-role"
				? {
					makeAssignee: false,
					hasSubroles: false,
					isSingleUser: false
				}
				: null
		} as Field

		newField.id = await api.fieldSave(newField)

		this.addField(newField)
		return newField.id
	}

	private async saveIntegration (integration: IntegrationWithSettings) {
		await integrationApi.integrationsSave(integration.settings!)

		const integrationData = this.integrations.find(i => i.integration.id === integration.integration.id)
		if (integrationData) {
			integrationData.settings = integration.settings
		}
	}
}
